import { encode } from 'querystring'
import useSWR from 'swr'
import * as Sentry from '@sentry/node'
import { urlToRoute } from './sentry'

import { APIError, toResponseError } from './errors'
import analytics from './analytics'

// export const BASE_URL = 'http://dev-nucleus.leadschool.in:3015/api/gtw/v1'
export const BASE_URL = '/api'
export const AUTH_URL = `${BASE_URL}/auth/teacherAppLite`

export function authConf(skipAuth) {
  if (skipAuth) return { basePath: AUTH_URL, headers: {} }

  const {
    usertoken: authorization,
    user: { su: [su] } = { su: [{}] },
    academicyearid,
  } = JSON.parse(localStorage.getItem('auth')) || {}

  const basePath = `${BASE_URL}/${su.schoolid}/teacherAppLite/${su.id}`

  return { basePath, headers: { academicyearid, authorization } }
}

function handleError(request) {
  return async function createError(fetchError) {
    try {
      if (fetchError instanceof Response)
        throw new APIError(await toResponseError(fetchError))
      else throw fetchError
    } catch (e) {
      if (!(e instanceof APIError) || e.status > 401) {
        Sentry.withScope((scope) => {
          scope.addEventProcessor((event) => {
            const [err = {}] = event.exception.values
            const { method, url } = request
            if (err.value)
              err.value += `: ${method || 'GET'} ${urlToRoute(url)} ${
                e.status || ''
              }`.trim()

            const { frames = [] } = err.stacktrace || {}
            const fileIndex = frames.findIndex((f) =>
              f.filename.match(/\.\/lib\/hooks\.js$/),
            )

            if (fileIndex > 0) frames.splice(fileIndex + 1)

            return event
          })

          Sentry.captureException(e, {
            contexts: { request, response: e.response || {} },
          })
        })
      }

      return Promise.reject(e)
    }
  }
}

export function fetcher(resource, opts = {}) {
  const { basePath, headers: authHeaders } = authConf(opts.skipAuth)
  const params = encode(opts.params || {})
  const url = `${basePath}${resource}${params && `?${params}`}`
  const { body } = opts
  const method = (opts.method || (body ? 'POST' : 'GET')).toUpperCase()

  const headers = {
    'Content-Type': 'application/json',
    ...authHeaders,
    // support for dynamic acad year
    ...(opts.ay > 0 ? { academicyearid: opts.ay } : {}),
    ...(opts.headers || {}),
  }

  const ALLOW_BODY = ['PUT', 'POST', 'PATCH'].includes(method) && !!body

  const JSON_BODY = Object.keys(headers).some(
    (h) =>
      h.toLowerCase() === 'content-type' &&
      headers[h].includes('application/json'),
  )

  const request = { url, method, ...(ALLOW_BODY ? { body } : {}), headers }
  return fetch(url, {
    method,
    redirect: 'follow',
    headers,
    ...(ALLOW_BODY ? { body: JSON_BODY ? JSON.stringify(body) : body } : {}),
  })
    .then((res) => {
      if (res.status === 401) {
        localStorage.removeItem('auth')
        analytics.reset()
        Sentry.setUser(null)
        window.location = '/'
      }

      if (!res.ok) return Promise.reject(res)

      if (opts.download) return res.blob()
      return res.json()
    })
    .catch(handleError(request))
}

export function useFetch(resource, options = {}) {
  const shouldFetch =
    typeof window !== 'undefined' && !!localStorage.getItem('auth')

  const { params, ...opts } = options
  const query = encode(params || {})
  const url = shouldFetch && `${resource}${query && `?${query}`}`

  const {
    data,
    error,
    isValidating,
    mutate: swrMutate,
  } = useSWR(resource && url, (endpoint) => fetcher(endpoint, opts), {
    revalidateOnFocus: false,
    focusThrottleInterval: 180e3, // every 3 minute
    errorRetryInterval: 30e3,
    errorRetryCount: 6,
    ...opts,
  })

  const mutate = (...args) => {
    const { basePath } = authConf(false)
    caches
      .open('offlineCache')
      .then((cache) =>
        cache.delete(`${window.location.origin}${basePath}${url}`),
      )
      .finally(() => swrMutate(...args))
  }

  // if data is not defined, the query has not completed
  const loading = !(data || (error && !isValidating))
  return [data, { error, isValidating, mutate, loading }]
}

export function chatFetcher(resource, body) {
  const {
    user: {
      mobile,
      su: [{ schoolid }],
    },
    academicyearid,
    usertoken: authorization,
  } = JSON.parse(localStorage.getItem('auth'))

  const basePath = `${BASE_URL}/${schoolid}/teacherAppLite/teacherChat`
  const url = `${basePath}${resource}`
  const method = body ? 'POST' : 'GET'
  const headers = {
    'Content-Type': 'application/json',
    academicyearid,
    authorization,
  }
  const ALLOW_BODY = method === 'POST' && !!body

  const request = { url, method, ...(ALLOW_BODY ? { body } : {}), headers }
  return fetch(url, {
    method,
    headers,
    ...(ALLOW_BODY ? { body: JSON.stringify({ mobile, ...body }) } : {}),
  })
    .then((res) => {
      if (!res.ok) return Promise.reject(res)
      return res.json()
    })
    .catch(handleError(request))
}

// Custom Data Hooks with transformed data

export function useDayPlans(usmid, { params, ay, ongoing }) {
  const [data, extra] = useFetch(`/homework/dayCompletion/${usmid}`, {
    params,
    ay,
  })

  return [
    (data?.data || []).map((d, i) => {
      const lastItem = (data?.data || [])[i - 1]
      let status = 'NOTSTARTED'
      if (d.is_skipped) status = 'SKIPPED'
      else if (d.day_plan_end_date && !d.is_skipped) status = 'COMPLETED'
      else if (lastItem?.day_plan_end_date || (i === 0 && ongoing === '1'))
        status = 'ONGOING'

      return {
        ...d,
        completed_on: d.skipped_on_date || d.day_plan_end_date || null,
        status,
      }
    }),
    extra,
  ]
}

export function fetchStaticResources(resource, body) {
  const url = resource;
  const method = body ? 'POST' : 'GET';
  const headers = {
    'Content-Type': 'application/json',
  }
  const ALLOW_BODY = method === 'POST' && !!body;

  return fetch(url, {
    method,
    headers,
    ...(ALLOW_BODY ? { body: JSON.stringify({ body }) } : {}),
  }).then((res) => res.json())
}
